import type { StylesConfig } from 'react-select';
import { getReneoSelectChipTonePalette, type ReneoSelectChipTone } from './reneo-select-chip-tones';
import { INPUT_CONTROL_HEIGHT_PX } from './input';
import type { ReneoSelectOption, ReneoSelectSize } from './ReneoSelect';

/** FilterBar `ReactSelectPopover` — cap selected-tag area at this many wrapped rows before scroll. */
const COMPACT_MULTI_TAG_MAX_ROWS = 3;
/** Matches `multiValue.minHeight` (16) + 1px border. */
const COMPACT_MULTI_TAG_ROW_HEIGHT_PX = 17;
const COMPACT_MULTI_TAG_VALUE_CONTAINER_MAX_HEIGHT_PX = 4 + COMPACT_MULTI_TAG_MAX_ROWS * COMPACT_MULTI_TAG_ROW_HEIGHT_PX + (COMPACT_MULTI_TAG_MAX_ROWS - 1);

/** Same vertical scale as {@link Input} / {@link INPUT_CONTROL_HEIGHT_PX} (`md` ↔ Input `default`). */
const SIZE_HEIGHTS: Record<ReneoSelectSize, number> = {
    xs: INPUT_CONTROL_HEIGHT_PX.xs,
    sm: INPUT_CONTROL_HEIGHT_PX.sm,
    md: INPUT_CONTROL_HEIGHT_PX.md,
    lg: INPUT_CONTROL_HEIGHT_PX.lg,
};

/** Used with react-select `theme.spacing.controlHeight` (default 38px fights 36px forms). */
export function getReneoSelectControlHeightPx(size: ReneoSelectSize): number {
    return SIZE_HEIGHTS[size];
}

const INDICATOR_ICON_PX: Record<ReneoSelectSize, number> = {
    xs: 12,
    sm: 14,
    md: 16,
    lg: 18,
};

const INDICATORS_PADDING_RIGHT: Record<ReneoSelectSize, number> = {
    xs: 2,
    sm: 3,
    md: 4,
    lg: 6,
};

const DROPDOWN_INDICATOR_PADDING: Record<ReneoSelectSize, string> = {
    xs: '0 4px',
    sm: '0 3px',
    md: '2px 5px',
    lg: '4px 7px',
};

const CLEAR_INDICATOR_PADDING: Record<ReneoSelectSize, string> = {
    xs: '0 2px',
    sm: '0 2px',
    md: '2px 4px',
    lg: '4px 5px',
};

function bodyFontSize(size: ReneoSelectSize): number {
    switch (size) {
        case 'xs':
            return 12;
        case 'sm':
        case 'md':
            return 13;
        case 'lg':
            return 14;
    }
}

function valueContainerPadding(size: ReneoSelectSize, isMulti: boolean): string {
    if (isMulti) {
        switch (size) {
            case 'xs':
                return '1px 4px';
            case 'sm':
                return '2px 6px';
            case 'md':
                return '4px 12px';
            case 'lg':
                return '6px 16px';
        }
    }
    switch (size) {
        case 'xs':
            return '1px 4px';
        case 'sm':
            return '2px 6px';
        /** Match Radix `SelectTrigger` default (`py-2 px-3`) so text sits like `FormSelect`. */
        case 'md':
            return '8px 12px';
        case 'lg':
            return '10px 16px';
    }
}

function optionVerticalPadding(size: ReneoSelectSize): string {
    switch (size) {
        case 'xs':
            return '4px 10px';
        case 'sm':
        case 'md':
            return '6px 12px';
        case 'lg':
            return '8px 14px';
    }
}

interface CreateReneoStylesOptions {
    /** When true, do not lock control height — multi-value rows must grow with tags. */
    multi?: boolean;
    /** When true with multi (e.g. `multiMaxVisibleTags`), keep control/chips vertically centered in one compact row. */
    compactMultiRow?: boolean;
    /** Smaller inline multi tags (FilterBar react-select popover). */
    compactMultiTags?: boolean;
    /** Checkbox column in dropdown options (FilterBar react-select popover). */
    optionCheckboxes?: boolean;
    /** Tints multi/single value chip colors (see `reneo-select-chip-tones`). */
    chipTone?: ReneoSelectChipTone;
    /** Horizontal alignment of the displayed value / placeholder (e.g. investment-case input tables). */
    valueAlign?: 'left' | 'right';
    /** Muted fill for read-only controls (distinct from disabled opacity). */
    readOnlyControl?: boolean;
    /** Dense table cell — lock to `Input` `sm` / `h-8` (32px), tighter padding (investment-case inputs). */
    tableCell?: boolean;
    /**
     * Floor for the portaled menu width (px). Used by FilterBar popovers so long option
     * labels are not clipped when the control briefly measures narrower than the popover.
     */
    menuMinWidth?: number;
}

export function createReneoStyles<TOption extends ReneoSelectOption>(
    size: ReneoSelectSize,
    options?: CreateReneoStylesOptions,
): StylesConfig<TOption, boolean> {
    const isMulti = options?.multi ?? false;
    const tableCell = options?.tableCell ?? false;
    const compactMultiRow = options?.compactMultiRow ?? false;
    const compactMultiTags = options?.compactMultiTags ?? false;
    const menuMinWidthFloor = options?.menuMinWidth ?? 288;
    const optionCheckboxes = options?.optionCheckboxes ?? false;
    const denseTableMulti = Boolean(tableCell && isMulti && compactMultiRow);
    const minHeight = tableCell ? INPUT_CONTROL_HEIGHT_PX.sm : SIZE_HEIGHTS[size];
    const tonePalette = options?.chipTone != null ? getReneoSelectChipTonePalette(options.chipTone) : undefined;
    /** Match Radix `SelectTrigger` / `border-input`, not primary (form parity). */
    const borderRest = 'hsl(var(--input))';
    const borderFocus = 'hsl(var(--ring))';
    const fontSize = denseTableMulti ? 10 : tableCell && !isMulti ? 14 : bodyFontSize(size);
    const iconPx = denseTableMulti ? 12 : tableCell ? INDICATOR_ICON_PX.xs : INDICATOR_ICON_PX[size];
    const lockControlHeight = !isMulti || denseTableMulti;
    const valueAlign = options?.valueAlign ?? 'left';
    const alignEnd = valueAlign === 'right';
    const readOnlyControl = options?.readOnlyControl ?? false;
    const controlBackground = tableCell ? '#ffffff' : readOnlyControl ? 'hsl(var(--muted))' : 'hsl(var(--input-background))';
    const valueContainerPad = tableCell && !isMulti ? '0 8px' : denseTableMulti ? '0 8px' : valueContainerPadding(size, isMulti);

    return {
        /** React-select defaults can add margin; keep flush with FormField siblings. */
        container: base => ({
            ...base,
            margin: 0,
            boxSizing: 'border-box',
        }),
        control: (base, state) => ({
            ...base,
            /** Prevent flex children (`min-height: auto`) from growing past locked height vs Input default (36px). */
            boxSizing: 'border-box',
            padding: 0,
            overflow: 'hidden',
            border: `1px solid ${borderRest}`,
            /** `rounded-md` = 0.375rem = 6px; keep consistent with Input/SelectTrigger. */
            borderRadius: 6,
            boxShadow: 'none',
            /**
             * Default react-select indicators are taller than our min heights; scale icons/padding
             * below and lock single-select height so xs–lg all stay on-scale.
             */
            ...(lockControlHeight
                ? {
                      minHeight,
                      height: minHeight,
                      maxHeight: minHeight,
                  }
                : compactMultiTags
                  ? {
                        maxHeight: COMPACT_MULTI_TAG_VALUE_CONTAINER_MAX_HEIGHT_PX + 8,
                        alignItems: 'flex-start',
                    }
                  : { minHeight }),
            minWidth: 0,
            width: '100%',
            backgroundColor: state.isDisabled ? 'hsl(var(--muted) / 0.5)' : controlBackground,
            cursor: state.isDisabled ? 'not-allowed' : readOnlyControl ? 'default' : 'text',
            alignItems: isMulti ? (compactMultiRow || compactMultiTags ? (compactMultiTags ? 'flex-start' : 'center') : 'flex-start') : 'center',
            opacity: state.isDisabled ? 0.5 : 1,
            ...(readOnlyControl ? { pointerEvents: 'none' as const } : {}),
            '&:hover': { borderColor: borderRest },
            /**
             * Match Input `focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50`.
             * border + 3px ring at 50% opacity.
             */
            ...(state.isFocused
                ? {
                      borderColor: borderFocus,
                      boxShadow: tableCell ? `0 0 0 2px hsla(var(--ring) / 0.35)` : `0 0 0 3px hsla(var(--ring) / 0.5)`,
                  }
                : {}),
        }),
        valueContainer: base => ({
            ...base,
            padding: compactMultiTags ? '2px 6px' : valueContainerPad,
            gap: compactMultiTags ? 1 : denseTableMulti ? 2 : size === 'xs' ? 2 : 4,
            /**
             * `compactMultiRow` (e.g. multiMaxVisibleTags) stays one line.
             * `compactMultiTags` (FilterBar) must wrap — ReneoSelect also sets compactMultiRow
             * for that mode, so prefer wrap when compactMultiTags is on.
             */
            flexWrap: compactMultiTags ? 'wrap' : compactMultiRow ? 'nowrap' : 'wrap',
            flexDirection: 'row',
            flex: '1 1 0%',
            minWidth: 0,
            minHeight: 0,
            maxHeight: compactMultiTags ? COMPACT_MULTI_TAG_VALUE_CONTAINER_MAX_HEIGHT_PX : undefined,
            overflowY: compactMultiTags ? 'auto' : undefined,
            overflowX: compactMultiTags ? 'hidden' : compactMultiRow && !denseTableMulti ? 'hidden' : undefined,
            /** For single-select, lock height to prevent any child from pushing past control. */
            ...(lockControlHeight
                ? denseTableMulti
                    ? {
                          height: minHeight - 2,
                          maxHeight: minHeight - 2,
                          overflowX: 'hidden' as const,
                          overflowY: 'hidden' as const,
                          flexWrap: 'nowrap' as const,
                      }
                    : { height: minHeight - 2, maxHeight: minHeight - 2, overflow: 'hidden', overflowX: 'hidden', overflowY: 'hidden', flexWrap: 'nowrap' }
                : {}),
            boxSizing: 'border-box',
            alignItems: compactMultiTags ? 'flex-start' : 'center',
            ...(alignEnd && !isMulti ? { justifyContent: 'flex-end' } : {}),
        }),
        singleValue: base => ({
            ...base,
            color: tonePalette?.labelColor ?? '#374151',
            fontSize,
            lineHeight: 1.25,
            margin: 0,
            ...(alignEnd ? { textAlign: 'right' as const, width: '100%' } : {}),
        }),
        multiValue: (base, { data }) => ({
            ...base,
            display: compactMultiTags ? 'inline-flex' : base.display,
            width: compactMultiTags ? 'auto' : base.width,
            maxWidth: compactMultiTags ? '100%' : denseTableMulti ? '100%' : compactMultiRow ? 280 : base.maxWidth,
            margin: compactMultiTags ? '0 1px 0 0' : denseTableMulti ? 0 : base.margin,
            minHeight: compactMultiTags ? 16 : denseTableMulti ? 16 : base.minHeight,
            ...(denseTableMulti
                ? {
                      boxSizing: 'border-box' as const,
                      height: 16,
                      maxHeight: 16,
                      padding: 0,
                      flex: '1 1 0%',
                      display: 'flex',
                      alignItems: 'center',
                      overflow: 'hidden',
                      fontSize: 10,
                      lineHeight: '14px',
                  }
                : {}),
            /**
             * `minWidth: 0` lets chips shrink in narrow single-row controls (CTR-287).
             * For `compactMultiTags`, keep content min width so chips wrap instead of
             * squashing onto one overflowing line.
             */
            ...(compactMultiTags ? {} : { minWidth: 0 }),
            backgroundColor: (data as any)?.disabled ? '#f8fafc' : '#f1f5f9',
            borderRadius: compactMultiTags ? 3 : denseTableMulti ? 4 : 6,
            border: '1px solid #e2e8f0',
            opacity: (data as any)?.disabled ? 0.6 : 1,
        }),
        multiValueLabel: (base, { data }) => ({
            ...base,
            color: (data as any)?.disabled ? '#64748b' : '#334155',
            fontSize: compactMultiTags || denseTableMulti ? 10 : fontSize,
            lineHeight: compactMultiTags || denseTableMulti ? '14px' : base.lineHeight,
            padding: compactMultiTags ? '0 2px' : denseTableMulti ? '0 4px' : size === 'xs' ? '2px 6px' : size === 'lg' ? '5px 10px' : '4px 8px',
            fontWeight: compactMultiTags ? 400 : 500,
            ...(compactMultiRow
                ? {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap' as const,
                  }
                : {}),
            /** Avoid flex-grow that fights wrap sizing for compact multi tags. */
            ...(compactMultiTags ? { flex: '0 1 auto', minWidth: 0 } : {}),
        }),
        multiValueRemove: base => ({
            ...base,
            cursor: 'pointer',
            padding: compactMultiTags || denseTableMulti ? '0 1px' : base.padding,
            color: tonePalette?.removeColor ?? '#64748b',
            borderRadius: compactMultiTags ? '0 3px 3px 0' : denseTableMulti ? '0 4px 4px 0' : '0 6px 6px 0',
            ...(compactMultiTags
                ? {
                      '& svg': {
                          width: 10,
                          height: 10,
                      },
                  }
                : denseTableMulti
                  ? {
                        '& svg': {
                            width: 8,
                            height: 8,
                        },
                    }
                  : {}),
            ':hover': tonePalette
                ? {
                      backgroundColor: tonePalette.removeHoverBg,
                      color: tonePalette.removeHoverColor,
                  }
                : {
                      backgroundColor: '#e2e8f0',
                      color: '#0f172a',
                  },
        }),
        input: base => ({
            ...base,
            margin: 0,
            padding: 0,
            fontSize,
            lineHeight: 1.25,
            /** Default theme `controlHeight: 38` + tall inner input was stretching past `h-10` peers. */
            height: denseTableMulti ? 16 : Math.ceil(fontSize * 1.25),
            minHeight: 0,
            maxHeight: denseTableMulti ? 16 : Math.ceil(fontSize * 1.5),
            alignSelf: 'center',
            ...(denseTableMulti ? { flex: '0 0 0px', width: 0, minWidth: 0, maxWidth: 0, opacity: 0, overflow: 'hidden' } : {}),
            ...(alignEnd ? { textAlign: 'right' as const } : {}),
        }),
        placeholder: base => ({
            ...base,
            color: '#9ca3af',
            fontSize,
            lineHeight: 1.25,
            margin: 0,
            ...(alignEnd ? { textAlign: 'right' as const, width: '100%' } : {}),
        }),
        menuList: base => ({
            maxHeight: 250,
            padding: '4px 0',
            overflowX: 'hidden',
            ...base,
        }),
        option: (base, state) => ({
            ...base,
            fontSize,
            padding: optionVerticalPadding(size),
            backgroundColor: optionCheckboxes
                ? state.isDisabled
                    ? '#f8fafc'
                    : state.isFocused
                      ? '#f9fafb'
                      : 'transparent'
                : state.isDisabled
                  ? '#f8fafc'
                  : state.isSelected
                    ? '#dbeafe'
                    : state.isFocused
                      ? '#f9fafb'
                      : 'transparent',
            color: optionCheckboxes ? (state.isDisabled ? '#9ca3af' : '#374151') : state.isDisabled ? '#9ca3af' : state.isSelected ? '#1d4ed8' : '#374151',
            fontWeight: optionCheckboxes ? 400 : state.isSelected ? 600 : 400,
            cursor: state.isDisabled ? 'not-allowed' : 'pointer',
            opacity: state.isDisabled ? 0.6 : 1,
            ':active': {
                backgroundColor: state.isDisabled ? '#f8fafc' : '#f3f4f6',
            },
            minWidth: 0,
            overflow: 'hidden',
        }),
        noOptionsMessage: base => ({
            ...base,
            fontSize,
            color: '#9ca3af',
        }),
        indicatorsContainer: base => ({
            ...base,
            paddingRight: INDICATORS_PADDING_RIGHT[size],
            flexShrink: 0,
            alignSelf: compactMultiTags ? 'flex-start' : 'stretch',
            alignItems: compactMultiTags ? 'flex-start' : 'center',
            paddingTop: compactMultiTags ? 2 : base.paddingTop,
            /** Lock container height to match control; flex children center inside. */
            height: lockControlHeight ? minHeight - 2 : 'auto',
            boxSizing: 'border-box',
        }),
        dropdownIndicator: base => ({
            ...base,
            padding: tableCell ? '0 2px' : DROPDOWN_INDICATOR_PADDING[size],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            /** Lock indicator box to icon + padding so it never pushes control height. */
            boxSizing: 'border-box',
            '& svg': {
                width: iconPx,
                height: iconPx,
                flexShrink: 0,
            },
        }),
        clearIndicator: base => ({
            ...base,
            padding: tableCell ? '0 1px' : CLEAR_INDICATOR_PADDING[size],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxSizing: 'border-box',
            '& svg': {
                width: iconPx,
                height: iconPx,
                flexShrink: 0,
            },
        }),
        loadingIndicator: base => ({
            ...base,
            padding: DROPDOWN_INDICATOR_PADDING[size],
            display: 'flex',
            alignItems: 'center',
            boxSizing: 'border-box',
            '& svg': {
                width: iconPx,
                height: iconPx,
                flexShrink: 0,
            },
        }),
        indicatorSeparator: base => ({
            ...base,
            display: 'none',
            marginTop: 0,
            marginBottom: 0,
        }),
        menu: base => {
            /**
             * The portaled menu's parent is sized to the control by react-select. Keep this
             * element at 100% of that parent; parsing the default `100%` as `100px` narrowed
             * wide controls to the minimum menu width.
             */
            return {
                ...base,
                marginTop: 4,
                borderRadius: 8,
                border: `1px solid ${borderRest}`,
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
                zIndex: 9999,
                width: '100%',
                minWidth: menuMinWidthFloor,
            };
        },
        menuPortal: (base: object) => ({
            ...base,
            zIndex: 99999,
            pointerEvents: 'all' as const,
        }),
    };
}
